/**
 * Social Sharing API Client
 *
 * Handles social sharing rewards including:
 * - Tracking chest result shares
 * - Tracking junk/burn result shares
 * - Tracking lottery result shares
 * - Getting daily share status
 */

import config from '@/config';

const BASE_URL = config.API_BASE_URL;

export type ShareType = 'chest' | 'junk' | 'lottery';

export interface ShareResponse {
  code: number;
  data: {
    canShare: boolean;
    pointsEarned: number;
    message: string;
  };
  message?: string;
}

export interface ShareStatusResponse {
  code: number;
  data: {
    chest: boolean;
    junk: boolean;
    lottery: boolean;
  };
  message?: string;
}

/**
 * Track a chest result share
 * Awards 100 points if not shared today
 * Uses JWT token from HTTP-only cookie
 */
export async function shareChestResult(): Promise<ShareResponse> {
  const response = await fetch(`${BASE_URL}/chest/share`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to share chest result: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Track a junk/burn result share
 * Awards 20 points if not shared today
 * Uses JWT token from HTTP-only cookie
 */
export async function shareJunkResult(): Promise<ShareResponse> {
  const response = await fetch(`${BASE_URL}/tx-order/share/junk`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to share junk result: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Track a lottery result share
 * Awards 100 points if not shared today
 * Uses JWT token from HTTP-only cookie
 */
export async function shareLotteryResult(): Promise<ShareResponse> {
  const response = await fetch(`${BASE_URL}/points-lottery/share`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to share lottery result: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get daily share status for all share types
 * Returns which types can still be shared today
 * Uses JWT token from HTTP-only cookie
 */
export async function getShareStatus(): Promise<ShareStatusResponse> {
  const response = await fetch(`${BASE_URL}/chest/share/status`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get share status: ${response.statusText}`);
  }

  return response.json();
}
